import { FC, useEffect } from 'react';
import {
  TRACKING_EVENTS,
  TRACKING_LIST_IDS,
} from '~/common/constants/tracking';
import {
  buildEcommerce,
  buildTrackingItemFromProduct,
  pushDataLayerEvent,
} from '~/common/utils/tracking';
import { Product } from '~/types/product';
import {
  heroWidth,
  SCRATCH_CARD_REFERENCE_WIDTH,
  SCRATCH_CARD_REFERENCE_WIDTH_MOBILE,
  ScratchHeroArt,
} from '~/common/constants/scratchDesigns';
import type { CSSProperties } from 'react';
import { ScratchTile } from './ScratchTile';
import { ScratchReveal } from './ScratchReveal';
import {
  ScratchCardBadge,
  ScratchCardBadgeGlow,
  ScratchCardBadgeMark,
  ScratchCardInner,
  ScratchCardFrame,
  ScratchCardOuter,
  ScratchGrid,
  ScratchHeroImage,
  ScratchPanel,
  ScratchScroll,
} from './styles';

interface ScratchCardProps {
  products: Product[];
  /** Framing artwork. Rendered here so it is positioned BY the card it hangs off. */
  hero: ScratchHeroArt[];
}

export const ScratchCard: FC<ScratchCardProps> = ({ products, hero }) => {
  const listId = TRACKING_LIST_IDS.scratchDropGrid;

  // Sold-out products stay in the grid with a sold-out treatment (`ScratchTile`), at the
  // client's request: a drop that sells through should still show what it was. They used
  // to be filtered out here. Availability is as fresh as the loader data (`CacheShort`).
  //
  // Mirrors `ProductsSection`'s `view_item_list`, but sends the whole grid rather than
  // slicing to 20: a drop is eight products, so the slice would never bite and pretending
  // otherwise would just hide the real cap if a drop ever ran long.
  useEffect(() => {
    if (!products.length) return;

    pushDataLayerEvent({
      event: TRACKING_EVENTS.viewItemList,
      ecommerce: buildEcommerce(
        products.map((product) =>
          buildTrackingItemFromProduct({ product, listId }),
        ),
        products[0].prices.currencyCode,
        listId,
      ),
    });
  }, [products, listId]);

  return (
    <ScratchCardFrame>
      {/* Eager: above the fold and part of the LCP region. Each graphic's centre sits on a
          corner or edge midpoint of the card, so half of it hangs outside.

          A SIBLING of the card, not a child: an element's border and background paint
          before all of its positioned children, so from inside the card no z-index could
          ever put the card's border in front of the artwork. */}
      {hero.map((art) => (
        <ScratchHeroImage
          key={art.src}
          src={art.src}
          alt={art.alt}
          loading="eager"
          $anchor={art.anchor}
          $mobileAnchor={art.mobileAnchor}
          $mobileAboveCard={Boolean(art.mobileAboveCard)}
          style={
            {
              '--hero-w': heroWidth(art.width, SCRATCH_CARD_REFERENCE_WIDTH),
              '--hero-w-sm': heroWidth(
                art.mobileWidth,
                SCRATCH_CARD_REFERENCE_WIDTH_MOBILE,
              ),
              ...(art.rotation === undefined
                ? {}
                : { '--hero-rotate': `${art.rotation}deg` }),
            } as CSSProperties
          }
        />
      ))}

      <ScratchCardOuter>
        <ScratchCardInner>
          <ScratchPanel>
            {/* Inside the panel, so the coating covers the product grid only: the card's
                frame and the brokenplanet badge stay clean. */}
            <ScratchReveal />

            <ScratchScroll>
              <ScratchGrid>
                {products.map((product, index) => (
                  <ScratchTile
                    key={product.id}
                    product={product}
                    index={index}
                    listId={listId}
                  />
                ))}
              </ScratchGrid>
            </ScratchScroll>
          </ScratchPanel>
        </ScratchCardInner>

        <ScratchCardBadge>
          <ScratchCardBadgeGlow>
            <ScratchCardBadgeMark />
          </ScratchCardBadgeGlow>
        </ScratchCardBadge>
      </ScratchCardOuter>
    </ScratchCardFrame>
  );
};
